// =============================================================================
// FILE: packages/renderer/src/components/dashboard/DemoModeToggle.tsx
// PURPOSE: Founder switch to load the "Sierra Manor" sample dataset into the
//   live preview — powers dashboard counts, System Overview, and the
//   One-Click Submittal Set for launch videos & live demos.
// =============================================================================

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { isDemoMode, setDemoMode, DEMO_EVENT, SIERRA_MANOR_PROJECT_NAME } from '../../config/demoMode.js';

export function DemoModeToggle(): JSX.Element {
  const qc = useQueryClient();
  const [on, setOn] = useState<boolean>(() => isDemoMode());

  // Keep in sync if toggled elsewhere.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ on: boolean }>).detail;
      if (detail && typeof detail.on === 'boolean') setOn(detail.on);
    };
    window.addEventListener(DEMO_EVENT, handler);
    return () => window.removeEventListener(DEMO_EVENT, handler);
  }, []);

  const toggle = useCallback(() => {
    const next = !isDemoMode();
    setDemoMode(next);
    setOn(next);
    // Refresh anything sourced from the platform data graph.
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
  }, [qc]);

  return (
    <button
      type="button"
      data-testid="demo-mode-toggle"
      onClick={toggle}
      title={on
        ? `Demo Mode ON — showing "${SIERRA_MANOR_PROJECT_NAME}" sample data. Click to turn off.`
        : `Load the "${SIERRA_MANOR_PROJECT_NAME}" sample project (3 buildings · 10 racks · 100 cables)`}
      className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.18em] rounded-sm transition"
      style={{
        border: on ? '1px solid rgba(52,211,153,0.55)' : '1px solid rgba(120,140,170,0.35)',
        background: on ? 'rgba(52,211,153,0.14)' : 'rgba(30,41,59,0.4)',
        color: on ? '#6ee7b7' : 'rgba(148,172,210,0.7)',
        boxShadow: on ? '0 0 18px rgba(52,211,153,0.18)' : 'none',
      }}
    >
      <FlaskConical className="h-3.5 w-3.5" strokeWidth={1.8} />
      {on ? 'Demo Mode · On' : 'Demo Mode'}
      <span
        aria-hidden
        style={{
          width: 8, height: 8, borderRadius: '50%',
          background: on ? '#34d399' : 'rgba(120,140,170,0.5)',
          boxShadow: on ? '0 0 8px #34d399' : 'none',
        }}
      />
    </button>
  );
}
